import { useEffect, useMemo, useRef, useState } from "react";
import type { JSONContent } from "@tiptap/core";
import {
  ChevronDown,
  ChevronUp,
  ImagePlus,
  LoaderCircle,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { LoreEditor } from "@/editors/rich-text/lore-editor";
import {
  cloneEmptyEditorDocument,
  formatLoreErrorMessage,
  getLoreStatusVariant,
  isScopedLoreType,
  loreCanonStatuses,
  loreCanonStatusLabels,
  loreEntryTypeLabels,
  loreEntryTypes,
  type CreateLoreEntryInput,
  type LoreCanonStatus,
  type LoreEntryView,
  type UpdateLoreEntryInput,
} from "@/features/lore";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type LoreEntryFormProps = {
  createDraftStorageKey?: string | null;
  entry: LoreEntryView | null;
  initialDraft?: CreateLoreEntryInput | null;
  mode: "create" | "edit";
  onAutosave?: (input: UpdateLoreEntryInput) => Promise<LoreEntryView>;
  onCancelCreate?: () => void;
  onChangeCanonStatus?: (canonStatus: LoreCanonStatus) => Promise<void>;
  onCreate?: (input: CreateLoreEntryInput) => Promise<unknown>;
  onDelete?: () => void;
  onGenerateCover?: () => Promise<void>;
  onRemoveCover?: (input: UpdateLoreEntryInput) => Promise<void>;
  onUploadCover?: (input: UpdateLoreEntryInput) => Promise<void>;
};

type DraftState = {
  localRules: JSONContent;
  mainBody: JSONContent;
  title: string;
  type: LoreEntryView["type"];
};

type SaveState = "error" | "ready" | "saved" | "saving";

const SAVE_DELAY_MS = 800;
const LORE_ENTRY_HEADER_STORAGE_KEY_PREFIX = "lore:entry-header-collapsed:";
const LORE_CREATE_DRAFT_STORAGE_KEY_PREFIX = "lore:create-draft:";

function toDraftState(entry: LoreEntryView | null): DraftState {
  return toDraftStateWithInitial(entry, null);
}

function toDraftStateWithInitial(
  entry: LoreEntryView | null,
  initialDraft: CreateLoreEntryInput | null,
): DraftState {
  if (!entry && initialDraft) {
    return {
      localRules: initialDraft.localRules,
      mainBody: initialDraft.body,
      title: initialDraft.title,
      type: initialDraft.type,
    };
  }

  return {
    localRules: entry?.localRules ?? cloneEmptyEditorDocument(),
    mainBody: entry?.mainBody ?? cloneEmptyEditorDocument(),
    title: entry?.title ?? "",
    type: entry?.type ?? "culture",
  };
}

function serializeDraft(draft: DraftState) {
  return JSON.stringify(draft);
}

function getLoreCreateDraftStorageKey(key: string | null | undefined) {
  return key ? `${LORE_CREATE_DRAFT_STORAGE_KEY_PREFIX}${key}` : null;
}

function readStoredCreateDraft(key: string | null | undefined): DraftState | null {
  const storageKey = getLoreCreateDraftStorageKey(key);
  if (typeof window === "undefined" || !storageKey) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(storageKey);
    return rawValue ? JSON.parse(rawValue) as DraftState : null;
  } catch {
    return null;
  }
}

function writeStoredCreateDraft(key: string | null | undefined, draft: DraftState) {
  const storageKey = getLoreCreateDraftStorageKey(key);
  if (typeof window === "undefined" || !storageKey) {
    return;
  }

  window.sessionStorage.setItem(storageKey, serializeDraft(draft));
}

function clearStoredCreateDraft(key: string | null | undefined) {
  const storageKey = getLoreCreateDraftStorageKey(key);
  if (typeof window === "undefined" || !storageKey) {
    return;
  }

  window.sessionStorage.removeItem(storageKey);
}

function buildDraftState(
  entry: LoreEntryView | null,
  initialDraft: CreateLoreEntryInput | null,
  createDraftStorageKey: string | null | undefined,
) {
  if (!entry && !initialDraft) {
    const storedDraft = readStoredCreateDraft(createDraftStorageKey);
    if (storedDraft) {
      return storedDraft;
    }
  }

  return toDraftStateWithInitial(entry, initialDraft);
}

function readEntryCollapsedState(key: string) {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(`${LORE_ENTRY_HEADER_STORAGE_KEY_PREFIX}${key}`) === "true";
}

export function LoreEntryForm({
  createDraftStorageKey = null,
  entry,
  initialDraft = null,
  mode,
  onAutosave,
  onCancelCreate,
  onChangeCanonStatus,
  onCreate,
  onDelete,
  onGenerateCover,
  onRemoveCover,
  onUploadCover,
}: LoreEntryFormProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const entryResetRef = useRef(entry);
  const [draft, setDraft] = useState<DraftState>(() =>
    buildDraftState(entry, initialDraft, createDraftStorageKey),
  );
  const headerStorageKey = entry?.id ?? (mode === "create" ? "create" : "draft");
  const [createBusy, setCreateBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readEntryCollapsedState(headerStorageKey));
  const [coverLightboxOpen, setCoverLightboxOpen] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>(mode === "create" ? "ready" : "saved");
  const [statusBusy, setStatusBusy] = useState(false);
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState(() => serializeDraft(toDraftState(entry)));
  const draftSnapshot = useMemo(() => serializeDraft(draft), [draft]);
  const currentStatus = entry?.canonStatus ?? "draft";
  const canCreate = draft.title.trim().length > 0 && !createBusy;
  const hasLocalRulesSection = isScopedLoreType(draft.type);
  const toggleLabel = t("lore.entries.headerToggle", {
    defaultValue: collapsed ? "Expand entry header" : "Collapse entry header",
  });
  const statusContent = mode === "create"
    ? (createBusy
      ? (
        <>
          <LoaderCircle className="h-4 w-4 animate-spin" />
          {t("lore.entries.creating")}
        </>
      )
      : null)
    : saveState === "saving"
      ? (
        <>
          <LoaderCircle className="h-4 w-4 animate-spin" />
          {t("lore.save.saving")}
        </>
      )
      : saveState === "error"
        ? (
          <>
            <TriangleAlert className="h-4 w-4" />
            {t("lore.save.error")}
          </>
        )
        : null;

  useEffect(() => {
    entryResetRef.current = entry;
  }, [entry]);

  useEffect(() => {
    const nextDraft = buildDraftState(entryResetRef.current, initialDraft, createDraftStorageKey);
    setDraft(nextDraft);
    setCollapsed(readEntryCollapsedState(headerStorageKey));
    setLastSavedSnapshot(serializeDraft(nextDraft));
    setSaveError(null);
    setSaveState(mode === "create" ? "ready" : "saved");
    setCoverLightboxOpen(false);
    setCreateBusy(false);
    setMediaBusy(false);
    setStatusBusy(false);
  }, [createDraftStorageKey, entry?.id, headerStorageKey, initialDraft, mode]);

  useEffect(() => {
    if (mode !== "create" || initialDraft || !createDraftStorageKey) {
      return;
    }

    writeStoredCreateDraft(createDraftStorageKey, draft);
  }, [createDraftStorageKey, draft, initialDraft, mode]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      `${LORE_ENTRY_HEADER_STORAGE_KEY_PREFIX}${headerStorageKey}`,
      String(collapsed),
    );
  }, [collapsed, headerStorageKey]);

  useEffect(() => {
    if (mode !== "edit" || !entry || !onAutosave || saveState === "saving") {
      return;
    }

    if (draftSnapshot === lastSavedSnapshot) {
      return;
    }

    const timeout = window.setTimeout(async () => {
      setSaveState("saving");

      try {
        await onAutosave({
          title: draft.title,
          type: draft.type,
          body: draft.mainBody,
          localRules: draft.localRules,
        });
        setLastSavedSnapshot(draftSnapshot);
        setSaveError(null);
        setSaveState("saved");
      } catch (error) {
        setSaveError(formatLoreErrorMessage(error));
        setSaveState("error");
      }
    }, SAVE_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [draft, draftSnapshot, entry, lastSavedSnapshot, mode, onAutosave, saveState]);

  async function handleCreate() {
    if (!onCreate) {
      return;
    }

    setCreateBusy(true);
    setSaveError(null);

    try {
      await onCreate({
        title: draft.title,
        type: draft.type,
        body: draft.mainBody,
        localRules: draft.localRules,
      });
      clearStoredCreateDraft(createDraftStorageKey);
      setCreateBusy(false);
    } catch (error) {
      setSaveError(formatLoreErrorMessage(error));
      setCreateBusy(false);
    }
  }

  async function handleCanonStatusChange(canonStatus: string) {
    if (!entry || !onChangeCanonStatus) {
      return;
    }

    setStatusBusy(true);
    setSaveError(null);

    try {
      await onChangeCanonStatus(canonStatus as LoreCanonStatus);
    } catch (error) {
      setSaveError(formatLoreErrorMessage(error));
    } finally {
      setStatusBusy(false);
    }
  }

  async function handleUploadCover(file: File | null) {
    if (!file || !onUploadCover) {
      return;
    }

    setMediaBusy(true);
    setSaveError(null);

    try {
      await onUploadCover({
        title: draft.title,
        type: draft.type,
        body: draft.mainBody,
        localRules: draft.localRules,
        coverImageFile: file,
      });
    } catch (error) {
      setSaveError(formatLoreErrorMessage(error));
    } finally {
      setMediaBusy(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  async function handleRemoveCover() {
    if (!onRemoveCover) {
      return;
    }

    setMediaBusy(true);
    setSaveError(null);

    try {
      await onRemoveCover({
        title: draft.title,
        type: draft.type,
        body: draft.mainBody,
        localRules: draft.localRules,
        removeCoverImage: true,
      });
    } catch (error) {
      setSaveError(formatLoreErrorMessage(error));
    } finally {
      setMediaBusy(false);
    }
  }

  return (
    <section className={collapsed ? "space-y-2" : "space-y-6"}>
      <header className={collapsed
        ? "space-y-2 px-1 transition-all duration-300 ease-out"
        : "space-y-4 px-1 transition-all duration-300 ease-out"}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-3">
            <div className={collapsed ? "flex flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-2"}>
              <Badge
                variant="outline"
                className={collapsed
                  ? "rounded-full border-clay/15 bg-white/70 px-2.5 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] text-clay transition-all duration-300 ease-out"
                  : "rounded-full border-clay/15 bg-white/70 px-3 py-1 text-[0.7rem] uppercase tracking-[0.18em] text-clay transition-all duration-300 ease-out"}
              >
                {t(loreEntryTypeLabels[draft.type])}
              </Badge>
              <Badge
                className={collapsed
                  ? "rounded-full px-2.5 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] transition-all duration-300 ease-out"
                  : "rounded-full px-3 py-1 text-[0.7rem] uppercase tracking-[0.18em] transition-all duration-300 ease-out"}
                variant={getLoreStatusVariant(currentStatus)}
              >
                {t(loreCanonStatusLabels[currentStatus])}
              </Badge>
            </div>

            <div className="space-y-3">
              <Input
                aria-label={t("lore.fields.title")}
                id="lore-entry-title"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    title: event.target.value,
                  }))}
                placeholder={t("lore.fields.titlePlaceholder")}
                value={draft.title}
                className={collapsed
                  ? "h-auto rounded-none border-0 border-b border-ink/10 bg-transparent px-0 py-0 font-display text-[clamp(1.5rem,2vw,2rem)] leading-none shadow-none focus:border-clay/40 focus:ring-0"
                  : "h-auto rounded-none border-0 border-b border-ink/10 bg-transparent px-0 py-0 font-display text-[clamp(2.1rem,4vw,3.4rem)] leading-[0.95] shadow-none focus:border-clay/40 focus:ring-0"}
              />

              <div
                className={collapsed
                  ? "pointer-events-none max-h-0 overflow-hidden opacity-0 -translate-y-2 transition-all duration-300 ease-out"
                  : "max-h-32 overflow-hidden opacity-100 translate-y-0 transition-all duration-300 ease-out"}
              >
                <p className="max-w-4xl text-lg leading-8 text-clay">
                  {hasLocalRulesSection
                    ? t("lore.entries.localRulesScopedBody")
                    : t("lore.entries.worldScopeBody")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-3">
            <div className="flex items-start gap-3">
              <Button
                aria-label={toggleLabel}
                className="h-8 w-8 rounded-full border border-[#dcccb7] bg-white p-0 text-muted-foreground shadow-none transition-all duration-300 ease-out hover:bg-[#f8f3eb] hover:text-foreground"
                onClick={() => setCollapsed((current) => !current)}
                type="button"
                variant="ghost"
              >
                <span className="transition-transform duration-300 ease-out">
                  {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                </span>
              </Button>

              {statusContent ? (
                <p
                  aria-live="polite"
                  className="inline-flex items-center gap-2 pt-1 text-sm whitespace-nowrap text-muted-foreground transition-all duration-300 ease-out"
                >
                  {statusContent}
                </p>
              ) : null}
            </div>

            {collapsed && entry?.coverImageUrl ? (
              <button
                aria-label={t("lore.fields.coverAlt", { title: draft.title || t("lore.entries.untitled") })}
                className="block w-full max-w-[8.5rem] overflow-hidden rounded-[16px] border border-border/60 bg-white shadow-sm transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setCoverLightboxOpen(true)}
                type="button"
              >
                <img
                  alt={t("lore.fields.coverAlt", { title: draft.title || t("lore.entries.untitled") })}
                  className="h-24 w-full object-cover transition-all duration-300 ease-out hover:scale-[1.02]"
                  src={entry.coverImageUrl}
                />
              </button>
            ) : null}
          </div>
        </div>

        {saveError ? (
          <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {t(saveError)}
          </p>
        ) : null}
      </header>

      <div
        className={collapsed
          ? "pointer-events-none max-h-0 overflow-hidden border-t border-ink/10 pt-0 opacity-0 -translate-y-2 transition-all duration-300 ease-out"
          : "max-h-[26rem] overflow-hidden border-t border-ink/10 pt-6 opacity-100 translate-y-0 transition-all duration-300 ease-out"}
      >
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_12rem]">
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-[13rem_13rem_minmax(0,1fr)] md:items-end">
              <div className="space-y-2">
                <Label htmlFor="lore-entry-type">{t("lore.fields.type")}</Label>
                <Select
                  onValueChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      type: value as LoreEntryView["type"],
                    }))}
                  value={draft.type}
                >
                  <SelectTrigger aria-label={t("lore.fields.type")} id="lore-entry-type">
                    <SelectValue placeholder={t("lore.filters.allTypes")} />
                  </SelectTrigger>
                  <SelectContent>
                    {loreEntryTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(loreEntryTypeLabels[type])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lore-entry-status">{t("lore.fields.status")}</Label>
                {mode === "edit" ? (
                  <Select
                    disabled={statusBusy}
                    onValueChange={handleCanonStatusChange}
                    value={currentStatus}
                  >
                    <SelectTrigger aria-label={t("lore.fields.status")} id="lore-entry-status">
                      <SelectValue placeholder={t("lore.fields.status")} />
                    </SelectTrigger>
                    <SelectContent>
                      {loreCanonStatuses.map((status) => (
                        <SelectItem key={status} value={status}>
                          {t(loreCanonStatusLabels[status])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex h-11 items-center rounded-2xl border border-input bg-white/85 px-4 text-sm text-foreground">
                    {t("lore.statuses.draft")}
                  </div>
                )}
              </div>

              {mode === "edit" ? (
                <div className="flex flex-wrap items-end justify-start gap-2 md:justify-end">
                  {entry?.coverGenerationEligible && onGenerateCover ? (
                    <Button
                      disabled={mediaBusy}
                      onClick={() => void onGenerateCover()}
                      type="button"
                      variant="secondary"
                    >
                      <Sparkles className="h-4 w-4" />
                      {t(entry.coverGenerateActionLabel ?? "lore.actions.generateImage")}
                    </Button>
                  ) : null}
                  <input
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => void handleUploadCover(event.target.files?.[0] ?? null)}
                    ref={fileInputRef}
                    type="file"
                  />
                  <Button
                    disabled={mediaBusy}
                    onClick={() => fileInputRef.current?.click()}
                    type="button"
                    variant="outline"
                  >
                    {entry?.coverImagePath ? <Upload className="h-4 w-4" /> : <ImagePlus className="h-4 w-4" />}
                    {entry?.coverImagePath ? t("lore.actions.replaceCover") : t("lore.actions.uploadCover")}
                  </Button>
                  {entry?.coverImagePath ? (
                    <Button disabled={mediaBusy} onClick={() => void handleRemoveCover()} type="button" variant="ghost">
                      <X className="h-4 w-4" />
                      {t("lore.actions.removeCover")}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className="flex items-end">
                  <p className="text-xs leading-5 text-muted-foreground">
                    {t("lore.fields.coverCreateHint")}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex xl:justify-end">
            {entry?.coverImageUrl ? (
              <button
                aria-label={t("lore.fields.coverAlt", { title: draft.title || t("lore.entries.untitled") })}
                className="block w-full max-w-[11.5rem] overflow-hidden rounded-[20px] border border-border/60 bg-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setCoverLightboxOpen(true)}
                type="button"
              >
                <img
                  alt={t("lore.fields.coverAlt", { title: draft.title || t("lore.entries.untitled") })}
                  className="h-36 w-full object-cover transition-all duration-300 ease-out hover:scale-[1.02]"
                  src={entry.coverImageUrl}
                />
              </button>
            ) : (
              <div className="flex h-36 w-full max-w-[11.5rem] items-center justify-center rounded-[20px] border border-dashed border-border bg-white/65 px-4 text-center text-sm text-muted-foreground">
                {mediaBusy ? t("lore.messages.coverSaving") : t("lore.fields.coverEmpty")}
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <LoreEditor
          content={draft.mainBody}
          onChange={(mainBody) =>
            setDraft((current) => ({
              ...current,
              mainBody,
            }))}
          placeholder={t("lore.fields.bodyPlaceholder")}
        />
      </div>

      {hasLocalRulesSection ? (
        <div className="space-y-3">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-foreground">{t("lore.fields.localRules")}</h3>
            <p className="text-sm text-muted-foreground">{t("lore.fields.localRulesHelp")}</p>
          </div>
          <LoreEditor
            content={draft.localRules}
            onChange={(localRules) =>
              setDraft((current) => ({
                ...current,
                localRules,
              }))}
            placeholder={t("lore.fields.localRulesPlaceholder")}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {mode === "create" && onCancelCreate ? (
            <Button
              onClick={() => {
                clearStoredCreateDraft(createDraftStorageKey);
                onCancelCreate();
              }}
              type="button"
              variant="ghost"
            >
              {t("common.cancel")}
            </Button>
          ) : null}
          {mode === "edit" && onDelete ? (
            <Button onClick={onDelete} type="button" variant="ghost">
              <Trash2 className="h-4 w-4" />
              {t("lore.actions.delete")}
            </Button>
          ) : null}
        </div>

        {mode === "create" && onCreate ? (
          <Button disabled={!canCreate} onClick={() => void handleCreate()} type="button">
            {createBusy ? t("lore.entries.creating") : t("lore.entries.createAction")}
          </Button>
        ) : null}
      </div>

      <ImageLightbox
        alt={t("lore.fields.coverAlt", { title: draft.title || t("lore.entries.untitled") })}
        imageUrl={entry?.coverImageUrl ?? null}
        onOpenChange={setCoverLightboxOpen}
        open={coverLightboxOpen}
        title={draft.title || t("lore.entries.untitled")}
      />
    </section>
  );
}
